import { useMemo } from 'react';
import { mappedTypes, TransactionsTableProvider } from '../Chart/context/TransactionsTableContext';
import TransactionsTable, { Transaction } from './TransactionsTable';
import { ReportRow, ReportViewModel } from 'types/report';
import { getQueueCells } from '@hone-automation/common';
import { formatSingleDate } from 'lib/utils';
import { ReportTransactionDataRow } from '@hone-automation/common';
import { AttachmentMetadata } from 'hooks/useAttachmentsModal';
import { EnhancedAskRequest } from 'hooks/useTransactionDetails';
import { useQueryState } from 'hooks/useQueryState';
import { useActiveReportStore } from 'hooks/useActiveReportStore';

type TransactionsTableContainerProps = {
  rawData: ReportViewModel | null | undefined;
  selectedRow?: string | null;
  selectedColumn?: number | string | null;
  flaggedTransactions?: EnhancedAskRequest[] | undefined;
  showAttachment: (attachments: string[], attachmentMetadata: AttachmentMetadata) => void;
  onRowClick?: (transaction: Transaction) => void;
  resolvedLocationId?: string;
};

export default function TransactionsTableContainer({
  rawData,
  selectedRow,
  selectedColumn,
  flaggedTransactions,
  showAttachment,
  resolvedLocationId,
}: TransactionsTableContainerProps) {
  const [reverse] = useQueryState<string>('reverse');
  const [consolidated] = useQueryState<string>('consolidated');
  const currentReport = useActiveReportStore(state => state.currentReport);
  const allowFlagging = currentReport?.allowFlagging ?? true;

  const selectedItem: ReportRow | null = useMemo(() => {
    if (!rawData || selectedColumn === null || selectedRow === null) return null;

    const columnData = rawData.columns[(selectedColumn as number) + 1]?.data?.find(
      transaction => transaction.id === selectedRow
    );

    const fixedColumnData = {
      ...columnData,
      reportId: rawData.columns[(selectedColumn as number) + 1]?.reportId,
    };

    return fixedColumnData;
  }, [rawData, selectedColumn, selectedRow, reverse]);
  const dataRows = selectedItem?.dataRows || [];

  const queueIssues = useMemo(() => {
    // Get queue issues (AskRequest[]) using accessor
    return getQueueCells(rawData) || [];
  }, [rawData]);

  const transactions: Transaction[] | undefined = useMemo(() => {
    if (!dataRows || !Array.isArray(dataRows)) return [];
    const transformedTransactions = dataRows.map((trx: any) => {
      const { amount, description, eventId, name, num, txnDate, type, dueDate, pastDue, balance } = trx;
      return {
        period: trx.txnDate,
        date: formatSingleDate(txnDate)!,
        type: mappedTypes[type as keyof typeof mappedTypes] || type,
        name,
        reference: num || eventId,
        memo: description || '',
        dueDays: pastDue ? pastDue : 'paid',
        dueDate: formatSingleDate(dueDate),
        amount: parseFloat(amount),
        links: 0,
        balance: parseFloat(balance),
        eventId,
      };
    });
    return transformedTransactions || [];
  }, [selectedItem]);

  const total = selectedItem?.amount ? selectedItem.amount : transactions?.reduce((sum, t) => sum + t.amount, 0);
  const balance = transactions?.reduce((sum, t) => sum + (t.balance || 0), 0);
  const type = rawData?.type;

  return (
    <TransactionsTableProvider
      key={`${selectedRow}-${selectedColumn}`}
      initialTransactions={transactions}
      reportType={type}
      total={total as number}
      balance={balance as number}
      dataRows={dataRows as ReportTransactionDataRow[]}
      initialflaggedTransactions={flaggedTransactions}
      showAttachment={showAttachment}
      intitialSelectedTransaction={selectedItem}
      initialFlaggedCells={queueIssues!}
      resolvedLocationId={resolvedLocationId}
      allowFlagging={allowFlagging}
      isConsolidated={consolidated === 'true'}
    >
      <TransactionsTable />
    </TransactionsTableProvider>
  );
}
